// ============================================================
// useProfileManagement - target template handlers
// ============================================================
// Extracted from DeathWatchSimulator.jsx.
//
// The attacker-profile half moved to the roster in v6.47.0: a "profile"
// is now a combatant, so save/load/new/delete are roster operations and
// live in the composition root beside the other roster handlers.
// ============================================================

import deepClone from '../utils/deepClone';
import { createTab } from '../engine/factories';

/** How many copies a prompt answer asks for: at least one, and one for anything unreadable. */
const copiesAsked = (answer) => Math.max(1, parseInt(answer) || 1);

/**
 * `count` fresh tabs from a template - full wounds, no criticals or
 * conditions - numbered when there is more than one.
 */
function tabsFromTemplate(template, count) {
  return Array.from({ length: count }, (_, i) => {
    const tgt = deepClone(template);
    tgt.wounds.current = tgt.wounds.max;
    tgt.criticalDamage = [];
    tgt.conditions = [];
    if (count > 1) tgt.name = template.name + ' ' + (i + 1);
    const t = createTab(tgt, 'tab-' + Date.now() + '-' + i);
    if (count > 1) t.label = template.name + ' ' + (i + 1);
    return t;
  });
}

/** The templates without one name. */
function withoutTemplate(templates, name) {
  const next = { ...templates };
  delete next[name];
  return next;
}

/**
 * @param {Object} params
 * @param {Object}      params.tgtTemplates
 * @param {Function}    params.setTgtTemplates
 * @param {Object|null} params.currentTab
 * @param {Function}    params.setTabs
 * @param {Function}    params.setActiveTab
 * @param {Function}    params.setShowTgtMenu
 * @param {Function}    params.setNewTgtName
 * @param {Function}    params.isLocked
 * @param {Function}    params.showDialogAsync
 * @param {Function}    params.syncTargetToFirestore
 */
export default function useProfileManagement(params) {
  const { setTgtTemplates, currentTab, setShowTgtMenu, setNewTgtName, isLocked } = params;
  const handleSaveTgtTemplate = (name) => {
    if (!name || !currentTab || isLocked('targetTemplates')) return;
    setTgtTemplates((p) => ({ ...p, [name]: deepClone(currentTab.target) }));
    setShowTgtMenu(false);
    setNewTgtName('');
  };
  const handleDeleteTgtTemplate = (name) => {
    if (!name || isLocked('targetTemplates')) return;
    setTgtTemplates((p) => withoutTemplate(p, name));
  };
  const handleLoadTgtTemplate = (name) => loadTemplate(params, name);
  return {
    handleSaveTgtTemplate,
    handleDeleteTgtTemplate,
    handleLoadTgtTemplate,
  };
}

/** Load a template as new tabs, asking how many copies; the first becomes active. */
async function loadTemplate(p, name) {
  if (p.isLocked('targetTemplates') || p.isLocked('manageTabs')) return;
  const template = p.tgtTemplates[name];
  if (!template) return;
  p.setShowTgtMenu(false);
  const countStr = await p.showDialogAsync('prompt', 'How many copies of "' + name + '"?', '1');
  if (countStr === null) return;
  const newTabs = tabsFromTemplate(template, copiesAsked(countStr));
  p.setTabs((prev) => [...prev, ...newTabs]);
  p.setActiveTab(newTabs[0].id);
  newTabs.forEach((t) => p.syncTargetToFirestore(t.id, t.target));
}
